import React, { useEffect, useState } from 'react'
import chevDown from 'assets/chevron-down.svg'
import CustomInput from 'pages/auth/CustomInput'
import { FormCreatableSelects, FormSelects } from 'components/FormInputSelect'
import { Plus, XSquare } from 'assets'
import CustomButton from 'components/CustomButton'
import LaguItemTable from './LaguItemTable'

// Kolom persentase pembagian hak. `errorKey` harus sama dengan `name` yang
// dipakai AddLagu/Detail saat mengisi isErrorPembagian.
const PEMBAGIAN_FIELDS = [
  { name: 'mech', label: 'Mekanikal', errorKey: 'Mech' },
  { name: 'perform', label: 'Performing', errorKey: 'Perform' },
  { name: 'sync', label: 'Sync', errorKey: 'Synchronize' },
  { name: 'demd', label: 'DEMD', errorKey: 'Demd' },
]

// Daftar DSP datang dari tabel referensi `publisher.rt_dsp` lewat prop
// `dspOptions` — dulu dipatok di sini, jadi menambah DSP berarti rilis ulang.
// Nama field-nya `dsp_code` supaya isi kartu ini bisa langsung dikirim sebagai
// payload `asset` tanpa penerjemahan.
const asetIdFields = (dspOptions) => [
  { name: 'asset_id', label: 'Asset ID', placeholder: 'Masukan Asset ID', required: true },
  { name: 'dsp_code', label: 'DSP', placeholder: 'Select DSP', type: 'select', options: dspOptions },
]

const ALIAS_FIELDS = [
  { name: 'judul', label: 'Judul Alias', placeholder: 'Masukan Judul Alias' },
]

const InfoLagu = ({
  page,
  pencipta,
  penciptaHeader,
  penciptaHeaderObject,
  dataPencipta,
  judulLain,
  informasiLagu,
  dataPenyanyi,
  isErrorPembagian,
  setPage,
  setPencipta,
  setPenciptaHeader,
  setPenciptaHeaderObject,
  setDataPencipta,
  setJudulLain,
  setInformasiLagu,
  setDataPenyanyi,
  creator,
  asetId = [],
  dspOptions = [],
  // Nama pencipta dikunci: ditampilkan sebagai teks, bukan dropdown. Dipakai
  // di pendaftaran/revisi oleh pencipta, yang hanya boleh menamai dirinya dan
  // publisher yang menaunginya — dua nilai tetap, jadi dropdown tidak ada
  // gunanya dan justru memotong namanya jadi "I-007560847-6 - L...".
  isNamaLocked = false,
  // Formulir sedang diisi songwriter-nya sendiri (pengajuan atau revisi).
  isSongwriter = false,
  // Work ID boleh disunting. Hanya benar untuk publisher/admin saat mode edit:
  // pada pendaftaran, backend selalu membuatnya sendiri.
  canEditWorkId = false,
  setAsetId
}) => {
  const isReadOnly = page === 'detail'

  const [error, setError] = useState({
    isNamaError: {
      state: false,
      msg: ""
    },
    isPembagianError: {
      state: false,
      msg: ""
    }
  })

  const informasiLaguFields = [
    {
      label: 'Judul Lagu',
      required: true,
      name: 'judul_lagu',
      placeholder: 'Masukan Judul Lagu',
      onChange: handleChangeInfo,
      value: informasiLagu?.judul_lagu || ""
    },
    {
      label: 'ISWC Number',
      name: 'iswc_number',
      placeholder: 'ISWC Number',
      onChange: handleChangeInfo,
      value: informasiLagu?.iswc_number || ""
    },
    {
      // Work ID = kolom `id` lagu. Dibuat otomatis saat lagu didaftarkan, tapi
      // publisher boleh memperbaikinya lewat Edit Lagu — mis. menyamakannya
      // dengan Custom ID pada laporan DSP, yang memang kunci pencocokannya.
      //
      // Songwriter tidak pernah bisa mengubahnya, dan tanda wajibnya pun
      // dihilangkan untuknya: menandai field yang tidak bisa ia isi sebagai
      // wajib hanya membuatnya mengira ada yang kurang. Kewenangannya juga
      // ditegakkan backend (update_song), bukan hanya di sini.
      label: 'Work ID',
      required: !isSongwriter,
      name: 'work_id',
      placeholder: isSongwriter
        ? 'Dibuat otomatis setelah lagu disetujui'
        : canEditWorkId
          ? 'Masukan Work ID'
          : 'Dibuat otomatis oleh sistem',
      isAlwaysDisabled: !canEditWorkId,
      onChange: canEditWorkId ? handleChangeInfo : undefined,
      value: informasiLagu?.work_id || ""
    },
    {
      label: 'Link YouTube',
      name: 'link_youtube',
      placeholder: 'Masukan link YouTube',
      onChange: handleChangeInfo,
      value: informasiLagu?.link_youtube || ""
    }
  ]

  const options = [
    {
      label: 'Composer',
      value: 'Composer'
    },
    {
      label: 'Lirik',
      value: 'Lirik'
    },
    {
      label: 'Publisher',
      value: 'Publisher'
    }
  ]

  const customStylesNama = {
    control: (provided, state) => ({
      ...provided,
      borderRight: "1px solid var(--border-strong)",
      borderRadius: "8px",
      background: 'transparent',
      outline: 'none'
    }),
    input: (provided, state) => ({
      ...provided,
      margin: "0px",
      padding: "16px",
      color: 'var(--text-primary)',
      width: "176px",
      border: "none",
      outline: "none"
    }),
    singleValue: (state) => ({
      marginLeft: '16px',
      gridArea: '1/1/2/3',
      maxWidth: '100%',
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      color: "hsl(0, 0%, 20%)",
      marginLeft: "16px",
      marginRight: "2px",
      boxSizing: "border-box"
    }),
    placeholder: (provided, state) => ({
      ...provided,
      margin: "0px",
      paddingLeft: "16px",
    }),
    indicatorSeparator: (state) => ({
      display: "none",
    }),
  };

  const customStyles = {
    control: (provided, state) => ({
      ...provided,
      border: 'none',
      borderRight: "1px solid var(--border-strong)",
      borderRadius: "0px",
      background: 'transparent',
      outline: 'none'
    }),
    input: (provided, state) => ({
      ...provided,
      margin: "0px",
      padding: "4px 4px 4px 16px",
      width: "176px",
      border: "none",
      outline: "none"
    }),
    placeholder: (provided, state) => ({
      ...provided,
      margin: "0px",
      paddingLeft: "16px",
    }),
    singleValue: (state) => ({
      marginLeft: '16px',
      gridArea: '1/1/2/3',
      maxWidth: '100%',
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      color: "hsl(0, 0%, 20%)",
      marginLeft: "16px",
      marginRight: "2px",
      boxSizing: "border-box"
    }),
    indicatorSeparator: (state) => ({
      display: "none",
    }),
  };


  // Satu tampilan untuk baris publisher maupun baris pencipta. Nilainya dibaca
  // langsung dari state, tidak lewat pencocokan daftar opsi seperti react-select
  // — jadi nama yang tidak ada di daftar pun tetap tampil, bukan jadi kosong.
  const namaPenciptaStatic = (value) => (
    <div className='nama-pencipta-static' title={value || ''}>
      {value || '-'}
    </div>
  )

  const onChangeSelect = (value, index, name, newOption) => {
    if (name === 'pencipta') {
      // console.log(value)
      if (newOption) {
        if (index >= 0) {
          const currentData = [...dataPencipta]
          currentData[index].namaPencipta = newOption
          return setDataPencipta((prev) => currentData)
        } else {
          setPenciptaHeaderObject({
            creator_id: newOption,
            email: newOption,
            name: newOption
          })
          return setPenciptaHeader((prev) => ({ ...prev, namaPencipta: newOption, creator_id: newOption }))
        }
      } else {
        if (index >= 0) {
          const currentData = [...dataPencipta]
          currentData[index].namaPencipta = value.label
          currentData[index].creator_id = value.id
          return setDataPencipta((prev) => currentData)
        } else {
          setPenciptaHeaderObject(value)
          return setPenciptaHeader((prev) => ({ ...prev, namaPencipta: value.label, creator_id: value.id }))
        }
      }
    } else {

      if (index >= 0) {
        const currentData = [...dataPencipta]
        currentData[index].sebagai = value.value
        return setDataPencipta((prev) => currentData)
      }
      const isErrorNama = errorChecker(value, 'sebagai')
      // console.log(value)
      return setPenciptaHeader((prev) => ({ ...prev, sebagai: value.value }))
    }
  }

  const onCreateOption = async (index, newOption) => {
    await setPencipta((prev) => ([...prev, { label: newOption, value: newOption, id: newOption }]))
    onChangeSelect({}, index, 'pencipta', newOption)
  }

  // Empat kolom persentase pembagian hak untuk satu baris pencipta.
  // `index` -1 berarti baris Publisher (penciptaHeader); hanya baris itu yang
  // membawa label kolom, sehingga label tampil sekali di atas semua baris.
  const renderPembagianInputs = (row, index) =>
    PEMBAGIAN_FIELDS.map((field) => {
      const isError = Boolean(checkErrorPembagian(isErrorPembagian, 'style', field.errorKey))
      const isHeaderRow = index === -1
      const inputId = `pembagian-${field.name}`

      return (
        <div key={field.name} className={`input-number-wrapper ${isError ? 'is-error' : ''}`}>
          {isHeaderRow && (
            <label htmlFor={inputId} className='label-number'>{field.label}</label>
          )}
          <input
            id={isHeaderRow ? inputId : undefined}
            aria-label={isHeaderRow ? undefined : `${field.label} (%)`}
            min={0}
            type='number'
            className='input-number'
            name={field.name}
            value={row[field.name]}
            disabled={isReadOnly}
            onChange={(e) => handleChangePencipta(e, index, 'pembagian')}
          />
          <span className='input-number-suffix' aria-hidden='true'>%</span>
        </div>
      )
    })

  const inputGroup = (data, index) => (
    <div className='group-input-lagu' key={index}>
      {isNamaLocked ?
        namaPenciptaStatic(dataPencipta[index].namaPencipta)
        : page === 'detail' ?
        <CustomInput
          type="text"
          placeholder="Masukan Nama"
          name='namaPencipta'
          value={dataPencipta[index].namaPencipta}
          disabled={page === 'detail' ? true : false}
          onChange={(e) => handleChangePencipta(e, index)}
        />
        :
        <FormCreatableSelects
          placeholder='Masukan Nama'
          formatCreateLabel={(string) => `Send email to '${string}'`}
          onCreateOption={(string) => onCreateOption(index, string)}
          name='namaPencipta'
          options={pencipta}
          value={dataPencipta[index].namaPencipta}
          onChange={(val) => onChangeSelect(val, index, 'pencipta')}
          styles={customStylesNama}
        />
      }
      <div className='role-input-wrapper'>
        <FormSelects
          placeholder="Pilih Sebagai"
          styles={customStyles}
          options={creator.filter((x) => x.label !== 'Publihser')}
          isDisabled={page === 'detail' ? true : false}
          value={dataPencipta[index].sebagai}
          onChange={(val) => onChangeSelect(val, index)}
        />
        {renderPembagianInputs(dataPencipta[index], index)}
      </div>
      {page !== 'detail' &&
        <div className='row-action' role='button' onClick={() => removePencipta(index)}>
          <XSquare />
        </div>
      }
    </div>
  )

  const tambahPenyanyi = () => {
    setDataPenyanyi((prev) => [...prev,
    {
      nama_penyanyi: '',
      isrc_number: ''
    }])
  }

  const tambahPencipta = () => {
    setDataPencipta((prev) => [...prev, {
      creator_id: "",
      namaPencipta: "",
      sebagai: "",
      mech: 0,
      perform: 0,
      sync: 0,
      demd: 0
    }])
  }

  function removePenyanyi(index) {
    const newData = [...dataPenyanyi]
    const removedItem = newData.splice(index, 1)
    setDataPenyanyi(newData)
  }

  function removePencipta(index) {

    const newData = [...dataPencipta]
    const removedItem = newData.splice(index, 1)
    setDataPencipta(newData)
  }

  function handleChangeInfo(e) {
    const { name, value } = e.target
    setInformasiLagu({ ...informasiLagu, [name]: value })
  }

  function handleChangePencipta(e, index, type) {
    const { name, value } = e.target
    const isErrorNama = errorChecker(value, name, index)
    if (index >= 0) {

      const currentData = [...dataPencipta]

      // console.log(currentData[index][name], value, type)

      if (type) {
        if (value[0] == '0' && value.length > 1 && !String(value).includes('.')) {
          currentData[index][name] = value.slice(1)
        } else {
          currentData[index][name] = value === '' ? 0 : value
        }
      } else {
        currentData[index][name] = value
      }
      return setDataPencipta((prev) => currentData)
    }
    // console.log('masuk bawah', { value, name, index, type })

    if (type) {
      if (value[0] == '0' && value.length > 1 && !String(value).includes('.')) {
        setPenciptaHeader({ ...penciptaHeader, [name]: value.slice(1) })
      } else {
        setPenciptaHeader({ ...penciptaHeader, [name]: value === '' ? 0 : value })
      }
    } else {
      // console.log('wikwik')
      setPenciptaHeader({ ...penciptaHeader, [name]: value })
    }
  }

  function handleChangePenyanyi(e, index) {
    const { name, value } = e.target
    const currentData = [...dataPenyanyi]
    currentData[index][name] = value
    return setDataPenyanyi((prev) => currentData)
  }

  function errorChecker(value, name, index) {
    const {
      namaPencipta,
      sebagai,
      mech,
      perform,
      demd,
      sync
    } = index >= 0 ? dataPencipta[index] : penciptaHeader

    if (value && name === 'sebagai') {
      if (!namaPencipta) {
        return true
      } else {
        return false
      }
    }

    if (value && name === 'namaPencipta') {
      if (!sebagai) {
        return true
      } else {
        return false
      }
    }

    if (!value || !namaPencipta || !sebagai) {
      // if (name !== 'namaPencipta' && name !== 'sebagai') {
      //   return false
      // }
      return true
    } else {
      return false
    }
  }

  function errorState() {
    if (penciptaHeader.isErrorNama) return true
    let isError = false
    dataPencipta.forEach((data) => {
      if (data.isErrorNama) {
        isError = true
      }
    })
    return isError
  }

  const checkErrorPembagian = (data, type, name) => {
    if (page === 'detail') return false
    if (!data) return false
    let isError = false
    if (type) {
      data.forEach((val) => {
        if (val.name === name) {
          if (val.isError) {
            isError = 'var(--danger)'
          } else {
            isError = ''
          }
        }
      })
    } else {
      data.forEach((val) => {
        if (val.isError) {
          isError = true
        }
      })
    }
    return isError
  }

  return (
    <div className='info-lagu-container'>
      <section className='form-wrapper lagu-card'>
        <div className='lagu-card-header'>
          <h3>Informasi Lagu</h3>
        </div>
        <div className='lagu-field-grid'>
          {informasiLaguFields.map((field) => (
            <CustomInput
              key={field.name}
              type='text'
              label={field.label}
              name={field.name}
              placeholder={field.placeholder}
              required={field.required && !isReadOnly}
              value={field.value}
              onChange={field.onChange}
              disabled={isReadOnly || field.isAlwaysDisabled}
            />
          ))}
        </div>
      </section>

      <LaguItemTable
        title='Aset ID'
        addLabel='Add Asset ID'
        addTitle='Add Asset ID'
        editTitle='Edit Asset ID'
        submitLabel='Add Asset ID'
        idPrefix='aset-id'
        fields={asetIdFields(dspOptions)}
        items={asetId}
        onChange={setAsetId}
        isReadOnly={isReadOnly || !setAsetId}
        emptyText='Belum ada Asset ID'
      />

      <LaguItemTable
        title='Judul Lagu Alias'
        addLabel='Add Alias'
        addTitle='Add Judul Lagu Alias'
        editTitle='Edit Judul Lagu Alias'
        submitLabel='Add'
        idPrefix='judul-alias'
        fields={ALIAS_FIELDS}
        items={judulLain.map((judul) => ({ judul }))}
        onChange={(rows) => setJudulLain(rows.map((row) => row.judul))}
        isReadOnly={isReadOnly}
        emptyText='Belum ada judul alias'
      />

      <section className='form-wrapper lagu-card'>
        <div className='lagu-card-header'>
          <h3>Perjanjian Lisensi</h3>
        </div>
        {
          errorState() && <div><span style={{ color: 'var(--danger)' }}><b>Nama</b>, atau <b>berperan sebagai apa</b> dalam perjanjian lisensi belum ditentukan, harap isi sesuai dengan kolom yang tersedia.</span></div>
        }
        {
          checkErrorPembagian(isErrorPembagian) && <div><span style={{ color: 'var(--danger)' }}><b>Pembagian hak {isErrorPembagian.map((val, index) => (<b key={val.name}>{index === isErrorPembagian.length - 1 && isErrorPembagian.length > 1 ? 'dan ' : ''}{val.name}{index !== isErrorPembagian.length - 1 && isErrorPembagian.length > 2 ? ',' : ''} </b>))}lebih atau kurang</b>, ubah pembagian hak tersebut agar tercapai jadi 100%</span></div>
        }
        <div className='input-container'>
          <div className='form-header'>
            <span style={{ flexGrow: '1' }}>Nama Songwriter</span>
            <div className='label-percent'>
            </div>
          </div>
          <div className='group-input-lagu'>
            {isNamaLocked ?
              namaPenciptaStatic(penciptaHeader.namaPencipta)
              : page === 'detail' || page === 'edit' ?
              <CustomInput
                type="text"
                placeholder="Masukan Nama"
                name="namaPencipta"
                value={penciptaHeader.namaPencipta}
                onChange={handleChangePencipta}
                disabled={true}
                errorBorder={error.isNamaError}
              />
              :
              <FormCreatableSelects
                placeholder='Masukan Nama'
                formatCreateLabel={(string) => `Send email to '${string}'`}
                onCreateOption={(string) => onCreateOption(-1, string)}
                name='namaPencipta'
                options={pencipta}
                isDisabled={true}
                value={penciptaHeader.namaPencipta}
                onChange={(val) => onChangeSelect(val, -1, 'pencipta')}
                styles={customStylesNama}
              />
            }
            <div className='role-input-wrapper'>
              <FormSelects
                placeholder="Pilih Sebagai"
                styles={customStyles}
                options={creator}
                isDisabled={true}
                value={penciptaHeader.sebagai}
              />
              {renderPembagianInputs(penciptaHeader, -1)}
            </div>
            {/* Baris Publisher tidak punya tombol hapus — perannya wajib. Ruang
                kosong selebar tombol itu tetap disediakan supaya kolom-kolomnya
                lurus dengan baris pencipta di bawah. */}
            {!isReadOnly && <div className='row-action' aria-hidden='true' />}
          </div>
          {dataPencipta.map((data, index) => inputGroup(data, index))}
          {!isReadOnly &&
            <button type='button' className='add-pencipta' onClick={tambahPencipta}>
              <Plus /> Tambah nama pencipta lagu
            </button>
          }
        </div>
        <div className='input-container'>
          <div className='nama-penyanyi'>
            <div className='form-label'>Nama Penyanyi</div>
            <div className='form-label'>ISRC Number</div>
            {!isReadOnly && <div className='row-action' aria-hidden='true' />}
          </div>
          {dataPenyanyi.map((data, index) => (
            <div className='nama-penyanyi' key={index}>
              <CustomInput
                type="text"
                placeholder="Masukan nama penyanyi lagu"
                name='nama_penyanyi'
                disabled={isReadOnly}
                value={dataPenyanyi[index].nama_penyanyi}
                onChange={(e) => handleChangePenyanyi(e, index)}
              />
              <CustomInput
                type="text"
                placeholder="Masukan ISRC Number"
                name="isrc_number"
                disabled={isReadOnly}
                value={dataPenyanyi[index].isrc_number}
                onChange={(e) => handleChangePenyanyi(e, index)}
              />
              {!isReadOnly && (
                index > 0 ? (
                  <div className='row-action' role='button' onClick={() => removePenyanyi(index)}>
                    <XSquare />
                  </div>
                ) : (
                  // Baris pertama tidak bisa dihapus; ruangnya tetap disediakan
                  // agar lebar kolomnya sama dengan baris di bawahnya.
                  <div className='row-action' aria-hidden='true' />
                )
              )}
            </div>
          ))}
          {!isReadOnly &&
            <button type='button' className='add-pencipta' onClick={tambahPenyanyi}>
              <Plus /> Tambah nama penyanyi lagu
            </button>
          }
        </div>
      </section>
    </div>
  )
}

export default InfoLagu